import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { api, BuilderSession, BuilderMessage, ClaudeMessageResponse } from '@/lib/api';
import { useStreaming } from '@/lib/hooks/useStreaming';
import { authApi } from '@/lib/auth-api';
import { COMPONENT_PATTERNS } from '../lib/component-patterns';
import { useUndoRedo } from './useUndoRedo';
import { loadHistory, saveHistory, cleanupOldEntries } from '../lib/history-store';

/** Verbose event for debugging */
export interface VerboseEvent {
  type: 'system_prompt' | 'tool_call' | 'tool_result' | 'model_info' | 'tools_available';
  timestamp: string;
  data: {
    /** For system_prompt */
    prompt?: string;
    /** For tool_call */
    toolName?: string;
    toolInput?: unknown;
    /** For tool_result */
    toolResult?: unknown;
    isError?: boolean;
    /** For model_info */
    model?: string;
    intent?: string;
    /** For tools_available */
    tools?: Array<{ name: string; description: string }>;
  };
}

interface UseBuilderSessionOptions {
  sessionId?: string;
  verbose?: boolean;
  /** When true, forces Claude Opus 4.5 for deeper reasoning */
  useOpus?: boolean;
  /** When set, skips intent classification and uses guided phase routing */
  guidedPhase?: string;
  onError?: (error: Error) => void;
  onVerboseEvent?: (event: VerboseEvent) => void;
}

/** UI component structure for direct manipulation */
interface UIComponent {
  type: string;
  id?: string;
  props?: Record<string, unknown>;
  style?: Record<string, unknown>;
  children?: UIComponent[];
  [key: string]: unknown;
}

/** UI definition structure */
interface UIDefinition {
  version?: string;
  root: UIComponent;
  actions?: Record<string, unknown>;
  input?: Record<string, unknown>;
  [key: string]: unknown;
}

interface UseBuilderSessionReturn {
  session: BuilderSession | null;
  preview: unknown | null;
  isLoading: boolean;
  isSending: boolean;
  isSaving: boolean;
  isResetting: boolean;
  error: Error | null;
  /** Streaming text being received (nodeId -> accumulated text) */
  streamingText: Record<string, string>;
  /** Whether streaming is complete */
  isStreamingComplete: boolean;
  sendMessage: (
    message: string,
    options?: { guidedPhaseOverride?: string },
  ) => Promise<ClaudeMessageResponse | null>;
  /** Stop the current generation */
  stopGeneration: () => void;
  saveSession: (
    name: string,
    description: string,
  ) => Promise<{ archId: string; version: number } | null>;
  updateSettings: (settings: unknown) => Promise<void>;
  resetContext: () => Promise<void>;
  refreshSession: () => Promise<void>;
  /** Add a component directly without Claude (bypasses AI) */
  addComponentDirect: (type: string, props?: Record<string, unknown>) => void;
  /** Apply a pre-validated pattern directly without Claude (bypasses AI) */
  applyPatternDirect: (patternName: string) => void;
  /** Set the UI definition directly (for local manipulation) */
  setUiDefinitionDirect: (definition: UIDefinition) => void;
  /** Undo the last UI change */
  undo: () => void;
  /** Redo a previously undone UI change */
  redo: () => void;
  /** Whether undo is available */
  canUndo: boolean;
  /** Whether redo is available */
  canRedo: boolean;
  /** Number of available undo steps */
  undoCount: number;
  /** Number of available redo steps */
  redoCount: number;
  /** Upload a preview screenshot to the server for AI verification */
  uploadPreviewScreenshot: (dataUrl: string) => void;
  /** Optimistically set the session name (header updates immediately) */
  setSessionName: (name: string) => void;
}

// WebSocket endpoint configuration - same as single-ai
const WEBSOCKET_ENDPOINT = import.meta.env.VITE_WEBSOCKET_ENDPOINT || '';

export function useBuilderSession({
  sessionId,
  verbose = false,
  useOpus = false,
  guidedPhase,
  onError,
  onVerboseEvent,
}: UseBuilderSessionOptions = {}): UseBuilderSessionReturn {
  const [session, setSession] = useState<BuilderSession | null>(null);
  const [preview, setPreview] = useState<unknown | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Undo/redo history for UI definitions
  const {
    canUndo,
    canRedo,
    undoCount,
    redoCount,
    snapshot: takeSnapshot,
    undo: undoHistory,
    redo: redoHistory,
    getHistory,
    restore: restoreHistory,
  } = useUndoRedo<unknown>({ maxHistory: 50 });
  const [isSending, setIsSending] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Streaming state - matches single-ai pattern
  const [currentRequestId, setCurrentRequestId] = useState<string | null>(null);
  const pendingMessageRef = useRef<string | null>(null);
  const resolveRef = useRef<((value: ClaudeMessageResponse | null) => void) | null>(null);

  // Get auth token synchronously - same as single-ai
  const authToken = authApi.getIdToken();

  // Use ref for onError to avoid recreating callbacks on every render
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  // Use ref for onVerboseEvent to avoid recreating callbacks
  const onVerboseEventRef = useRef(onVerboseEvent);
  onVerboseEventRef.current = onVerboseEvent;

  // Track verbose state
  const verboseRef = useRef(verbose);
  verboseRef.current = verbose;

  // Track useOpus state
  const useOpusRef = useRef(useOpus);
  useOpusRef.current = useOpus;

  // Track guidedPhase state
  const guidedPhaseRef = useRef(guidedPhase);
  guidedPhaseRef.current = guidedPhase;

  // Use refs for undo/redo to avoid stale closures in callbacks
  const takeSnapshotRef = useRef(takeSnapshot);
  takeSnapshotRef.current = takeSnapshot;

  // Refs for persistence helpers (avoid stale closures)
  const getHistoryRef = useRef(getHistory);
  getHistoryRef.current = getHistory;

  // Debounced save timer ref
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** Schedule a debounced persist of the current undo history to IndexedDB */
  const scheduleSave = useCallback(() => {
    if (!sessionId) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      const { states, index } = getHistoryRef.current();
      if (states.length > 0) {
        saveHistory(sessionId, states, index);
      }
    }, 1_000);
  }, [sessionId]);

  const scheduleSaveRef = useRef(scheduleSave);
  scheduleSaveRef.current = scheduleSave;

  // Load history from IndexedDB on mount (when sessionId is available)
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;

    (async () => {
      const saved = await loadHistory(sessionId);
      if (!cancelled && saved && saved.states.length > 0) {
        restoreHistory(saved.states, saved.index);
        // Also restore the preview to the current history position
        const currentState = saved.states[saved.index];
        if (currentState != null) {
          setPreview(currentState);
          setSession((prev) => (prev ? { ...prev, currentUiDefinition: currentState } : null));
        }
      }
    })();

    // Fire-and-forget cleanup of stale entries
    cleanupOldEntries();

    return () => {
      cancelled = true;
    };
    // Only run on mount / sessionId change — restoreHistory is stable
  }, [sessionId]);

  // Flush pending save on unmount
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        // Fire one final synchronous-ish save
        if (sessionId) {
          const { states, index } = getHistoryRef.current();
          if (states.length > 0) {
            saveHistory(sessionId, states, index);
          }
        }
      }
    };
  }, [sessionId]);

  // Use streaming hook - same pattern as single-ai
  const {
    streamingText,
    isComplete: streamingComplete,
    error: streamingError,
    reset: resetStreaming,
  } = useStreaming({
    requestId: currentRequestId,
    authToken: authToken,
    endpoint: WEBSOCKET_ENDPOINT,
    enabled: !!currentRequestId,
    // Handle real-time settings updates (e.g. guided phase transitions)
    onSettingsUpdate: useCallback((settings: Record<string, unknown>) => {
      setSession((prev) => {
        if (!prev) return null;

        // Prevent backward phase transitions from overwriting an optimistic
        // update (e.g. save_clarifying_qa broadcasting 'clarification' after
        // we already optimistically set 'planning')
        const PHASE_ORDER: Record<string, number> = {
          ideation: 0,
          clarification: 1,
          planning: 2,
          assumptions: 3,
          execution: 4,
          complete: 5,
        };
        const prevSettings = (prev.currentSettings as Record<string, unknown>) ?? {};
        const prevFlow = (prevSettings.guidedFlow as Record<string, unknown>) ?? {};
        const incomingFlow = (settings.guidedFlow as Record<string, unknown>) ?? {};
        const prevPhaseIdx = PHASE_ORDER[prevFlow.phase as string] ?? -1;
        const incomingPhaseIdx = PHASE_ORDER[incomingFlow.phase as string] ?? -1;

        if (incomingPhaseIdx < prevPhaseIdx) {
          // Keep the more advanced local phase but accept other settings changes
          const mergedFlow = { ...incomingFlow, phase: prevFlow.phase };
          return {
            ...prev,
            currentSettings: { ...settings, guidedFlow: mergedFlow },
          };
        }

        return { ...prev, currentSettings: settings };
      });
    }, []),
    // Handle real-time UI updates from Claude's tool calls
    onUiUpdate: useCallback((uiDefinition: unknown) => {
      // Update state first
      setPreview(uiDefinition);
      // Also update the session's UI definition for consistency
      setSession((prev) =>
        prev
          ? {
              ...prev,
              currentUiDefinition: uiDefinition,
            }
          : null,
      );
      // Take a snapshot AFTER updating (use ref to avoid stale closure)
      takeSnapshotRef.current(uiDefinition);
      // Persist to IndexedDB (debounced)
      scheduleSaveRef.current();
    }, []),
    // Handle verbose events (system prompt, tool calls)
    onSystemPrompt: useCallback((prompt: string) => {
      if (verboseRef.current && onVerboseEventRef.current) {
        onVerboseEventRef.current({
          type: 'system_prompt',
          timestamp: new Date().toISOString(),
          data: { prompt },
        });
      }
    }, []),
    onToolCall: useCallback((toolName: string, toolInput: unknown) => {
      if (verboseRef.current && onVerboseEventRef.current) {
        onVerboseEventRef.current({
          type: 'tool_call',
          timestamp: new Date().toISOString(),
          data: { toolName, toolInput },
        });
      }
    }, []),
    onToolResult: useCallback((toolName: string, toolResult: unknown, isError: boolean) => {
      if (verboseRef.current && onVerboseEventRef.current) {
        onVerboseEventRef.current({
          type: 'tool_result',
          timestamp: new Date().toISOString(),
          data: { toolName, toolResult, isError },
        });
      }
    }, []),
    onModelInfo: useCallback((model: string, intent: string) => {
      if (verboseRef.current && onVerboseEventRef.current) {
        onVerboseEventRef.current({
          type: 'model_info',
          timestamp: new Date().toISOString(),
          data: { model, intent },
        });
      }
    }, []),
    onToolsAvailable: useCallback((tools: Array<{ name: string; description: string }>) => {
      if (verboseRef.current && onVerboseEventRef.current) {
        onVerboseEventRef.current({
          type: 'tools_available',
          timestamp: new Date().toISOString(),
          data: { tools },
        });
      }
    }, []),
  });

  // Combine all node streaming text into one string (for display)
  const combinedStreamingText = useMemo(() => {
    return Object.values(streamingText).join('');
  }, [streamingText]);

  // Track streaming text in ref for access in stopGeneration
  const streamingTextRef = useRef<string>('');
  streamingTextRef.current = combinedStreamingText;

  // Handle streaming completion - separate effect like single-ai
  useEffect(() => {
    if (streamingComplete && currentRequestId) {
      // Streaming complete - refresh session to get final state
      const handleComplete = async () => {
        if (sessionId) {
          try {
            const response = await api.getBuilderSession(sessionId);
            setSession(response.session);
            setPreview(response.currentPreview);
          } catch (err) {
            console.error('Failed to refresh session after streaming', err);
          }
        }

        // Resolve the promise with a mock response
        if (resolveRef.current) {
          resolveRef.current({
            message: combinedStreamingText,
            artifacts: [],
            tokensUsed: { input: 0, output: 0 },
            creditsCost: 0,
          });
          resolveRef.current = null;
        }

        pendingMessageRef.current = null;
        setIsSending(false);
        setCurrentRequestId(null);
        resetStreaming();
      };

      handleComplete();
    }
  }, [streamingComplete, currentRequestId, sessionId, combinedStreamingText, resetStreaming]);

  // Handle streaming errors - separate effect like single-ai
  useEffect(() => {
    if (streamingError && currentRequestId) {
      const err = new Error(`Streaming error: ${streamingError.code} - ${streamingError.message}`);
      setError(err);
      onErrorRef.current?.(err);
      setIsSending(false);
      pendingMessageRef.current = null;
      setCurrentRequestId(null);
      resetStreaming();

      if (resolveRef.current) {
        resolveRef.current(null);
        resolveRef.current = null;
      }
    }
  }, [streamingError, currentRequestId, resetStreaming]);

  const fetchSession = useCallback(async () => {
    if (!sessionId) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await api.getBuilderSession(sessionId);
      setSession(response.session);
      setPreview(response.currentPreview);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch session');
      setError(error);
      onErrorRef.current?.(error);
    } finally {
      setIsLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  const sendMessage = useCallback(
    async (
      message: string,
      options?: { guidedPhaseOverride?: string },
    ): Promise<ClaudeMessageResponse | null> => {
      if (!sessionId || !session) return null;

      setIsSending(true);
      setError(null);
      resetStreaming();

      // Add user message to conversation immediately
      const userMessage: BuilderMessage = {
        role: 'user',
        content: message,
        timestamp: new Date().toISOString(),
      };

      // Optimistically update guided phase so the phase bar reflects the
      // transition immediately instead of waiting for the backend tool call
      if (options?.guidedPhaseOverride) {
        const overridePhase = options.guidedPhaseOverride;
        setSession((prev) => {
          if (!prev) return null;
          const settings = {
            ...((prev.currentSettings as Record<string, unknown>) ?? {}),
          };
          const flow = { ...((settings.guidedFlow as Record<string, unknown>) ?? {}) };
          flow.phase = overridePhase;
          settings.guidedFlow = flow;
          return {
            ...prev,
            currentSettings: settings,
            conversationHistory: [...prev.conversationHistory, userMessage],
          };
        });
      } else {
        setSession((prev) =>
          prev
            ? {
                ...prev,
                conversationHistory: [...prev.conversationHistory, userMessage],
              }
            : null,
        );
      }

      // Note: System prompt is now broadcast by the server when verbose mode is enabled
      // This ensures the displayed prompt matches what's actually sent to Claude

      try {
        // Start streaming (pass verbose, useOpus, and guidedPhase flags)
        const response = await api.startBuilderMessageStream(
          sessionId,
          message,
          verboseRef.current,
          useOpusRef.current,
          options?.guidedPhaseOverride ?? guidedPhaseRef.current,
        );
        pendingMessageRef.current = message;
        setCurrentRequestId(response.requestId);

        // Return a promise that resolves when streaming completes
        return new Promise<ClaudeMessageResponse | null>((resolve) => {
          resolveRef.current = resolve;
        });
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to send message');
        setError(error);
        onErrorRef.current?.(error);
        setIsSending(false);

        // Remove the user message if sending failed
        setSession((prev) =>
          prev
            ? {
                ...prev,
                conversationHistory: prev.conversationHistory.filter((m) => m !== userMessage),
              }
            : null,
        );

        return null;
      }
    },
    [sessionId, session, resetStreaming],
  );

  const saveSession = useCallback(
    async (
      name: string,
      description: string,
    ): Promise<{ archId: string; version: number } | null> => {
      if (!sessionId) return null;

      setIsSaving(true);
      setError(null);

      try {
        const response = await api.saveBuilderSession(sessionId, { name, description });

        // Update local session with archId
        setSession((prev) =>
          prev
            ? {
                ...prev,
                archId: response.archId,
                currentName: name,
                currentDescription: description,
              }
            : null,
        );

        return { archId: response.archId, version: response.version };
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to save session');
        setError(error);
        onErrorRef.current?.(error);
        return null;
      } finally {
        setIsSaving(false);
      }
    },
    [sessionId],
  );

  const updateSettings = useCallback(
    async (settings: unknown): Promise<void> => {
      if (!sessionId) return;

      setError(null);

      try {
        const updates: Parameters<typeof api.updateBuilderSession>[1] = {
          currentSettings: settings,
        };

        const response = await api.updateBuilderSession(sessionId, updates);

        // Merge response into existing session to preserve fields like conversationHistory
        setSession((prev) =>
          prev
            ? {
                ...prev,
                ...response.session,
                // Preserve conversationHistory if not in response
                conversationHistory:
                  response.session.conversationHistory ?? prev.conversationHistory,
              }
            : response.session,
        );
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to update settings');
        setError(error);
        onErrorRef.current?.(error);
      }
    },
    [sessionId],
  );

  const resetContext = useCallback(async (): Promise<void> => {
    if (!sessionId) return;

    setIsResetting(true);
    setError(null);

    try {
      const response = await api.resetBuilderContext(sessionId);

      // Update session with cleared conversation but preserved UI
      setSession(response.session);
      setPreview(response.currentPreview);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to reset context');
      setError(error);
      onErrorRef.current?.(error);
    } finally {
      setIsResetting(false);
    }
  }, [sessionId]);

  const stopGeneration = useCallback(() => {
    if (!isSending) return;

    // Capture the current streaming text before resetting
    const partialText = streamingTextRef.current;

    // If we have partial text, add it to conversation history as a stopped message
    if (partialText) {
      const partialMessage: BuilderMessage = {
        role: 'assistant',
        content: partialText + '\n\n_(Generation stopped)_',
        timestamp: new Date().toISOString(),
      };

      setSession((prev) =>
        prev
          ? {
              ...prev,
              conversationHistory: [...prev.conversationHistory, partialMessage],
            }
          : null,
      );
    }

    // Reset streaming state
    resetStreaming();
    setCurrentRequestId(null);
    setIsSending(false);
    pendingMessageRef.current = null;

    // Resolve any pending promise
    if (resolveRef.current) {
      resolveRef.current(null);
      resolveRef.current = null;
    }
  }, [isSending, resetStreaming]);

  /**
   * Generate a unique ID for a component
   */
  const generateComponentId = useCallback((type: string): string => {
    return `${type}-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 7)}`;
  }, []);

  /**
   * Add a component directly to the UI without invoking Claude
   * This is a "quick action" that bypasses the AI for simple additions
   */
  const addComponentDirect = useCallback(
    (type: string, props?: Record<string, unknown>) => {
      const currentDef = (session?.currentUiDefinition || preview) as UIDefinition | null;

      // Create new component with unique ID
      const newComponent: UIComponent = {
        type,
        id: generateComponentId(type),
        props: props || {},
      };

      let newDefinition: UIDefinition;

      if (!currentDef || !currentDef.root) {
        // No existing UI - create a container with the component
        newDefinition = {
          root: {
            type: 'container',
            props: { direction: 'column', gap: 16, padding: 16 },
            children: [newComponent],
          },
        };
      } else {
        // Add to existing root's children
        const rootChildren = currentDef.root.children || [];
        newDefinition = {
          ...currentDef,
          root: {
            ...currentDef.root,
            children: [...rootChildren, newComponent],
          },
        };
      }

      // Update local state
      setPreview(newDefinition);
      setSession((prev) =>
        prev
          ? {
              ...prev,
              currentUiDefinition: newDefinition,
            }
          : null,
      );
      // Take snapshot AFTER updating
      takeSnapshot(newDefinition);
      scheduleSave();
    },
    [session?.currentUiDefinition, preview, generateComponentId, takeSnapshot, scheduleSave],
  );

  /**
   * Apply a pre-validated pattern directly without invoking Claude
   * This replaces the current UI with the pattern's UI definition
   */
  const applyPatternDirect = useCallback(
    (patternName: string) => {
      const pattern = COMPONENT_PATTERNS[patternName];
      if (!pattern) {
        console.error(`Unknown pattern: ${patternName}`);
        return;
      }

      // Deep clone the pattern UI to avoid mutation
      const patternUi = JSON.parse(JSON.stringify(pattern.ui)) as UIDefinition;

      // Update local state
      setPreview(patternUi);
      setSession((prev) =>
        prev
          ? {
              ...prev,
              currentUiDefinition: patternUi,
            }
          : null,
      );
      // Take snapshot AFTER updating
      takeSnapshot(patternUi);
      scheduleSave();
    },
    [takeSnapshot, scheduleSave],
  );

  /**
   * Set the UI definition directly (for local manipulation)
   */
  const setUiDefinitionDirect = useCallback(
    (definition: UIDefinition) => {
      setPreview(definition);
      setSession((prev) =>
        prev
          ? {
              ...prev,
              currentUiDefinition: definition,
            }
          : null,
      );
      // Take snapshot AFTER updating
      takeSnapshot(definition);
      scheduleSave();
    },
    [takeSnapshot, scheduleSave],
  );

  /**
   * Undo the last UI change
   */
  const undo = useCallback(() => {
    const previousState = undoHistory();
    if (previousState !== null) {
      setPreview(previousState);
      setSession((prev) =>
        prev
          ? {
              ...prev,
              currentUiDefinition: previousState,
            }
          : null,
      );
      scheduleSave();
    }
  }, [undoHistory, scheduleSave]);

  /**
   * Upload a preview screenshot to the server for AI verification.
   * Fire-and-forget — errors are logged but do not affect user-facing state.
   */
  const uploadPreviewScreenshot = useCallback(
    (dataUrl: string) => {
      if (!sessionId) return;
      api.uploadPreviewScreenshot(sessionId, dataUrl).catch((err) => {
        console.warn('[useBuilderSession] Failed to upload preview screenshot:', err);
      });
    },
    [sessionId],
  );

  /**
   * Redo a previously undone UI change
   */
  const redo = useCallback(() => {
    const nextState = redoHistory();
    if (nextState !== null) {
      setPreview(nextState);
      setSession((prev) =>
        prev
          ? {
              ...prev,
              currentUiDefinition: nextState,
            }
          : null,
      );
      scheduleSave();
    }
  }, [redoHistory, scheduleSave]);

  return {
    session,
    preview,
    isLoading,
    isSending,
    isSaving,
    isResetting,
    error,
    streamingText,
    isStreamingComplete: streamingComplete,
    sendMessage,
    stopGeneration,
    saveSession,
    updateSettings,
    resetContext,
    refreshSession: fetchSession,
    addComponentDirect,
    applyPatternDirect,
    setUiDefinitionDirect,
    undo,
    redo,
    canUndo,
    canRedo,
    undoCount,
    redoCount,
    uploadPreviewScreenshot,
    setSessionName: useCallback(
      (name: string) => setSession((prev) => (prev ? { ...prev, currentName: name } : prev)),
      [],
    ),
  };
}
